import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getFormSyncErrors, touch } from 'redux-form';
import { Row, Col, Button, Icon, Tooltip, Modal, Badge } from '@syneto/compass-react';
import { useNavigate } from 'react-router-dom';
import Form from '../../../../components/forms/form';
import SelectOptionsRenderer from '../../../../helpers/SelectOptionsRenderer';
import { getActivationCode, getProductById, getProductSuccess } from '../../../../data/product/actions';
import { pushSuccessfulNotification } from '../../../../data/notifications/actions';
import ProductAddon from './ProductAddon';
import Utils from '../../../../helpers/Utils';
import ProductMaintenancePlan from './ProductMaintenancePlan';
import { WarningModal } from './WarningModal';
import CommunityReplacementNotice from './CommunityReplacementNotice';
import ProductValidationService from '../../validation/ProductValidationService';
import { applyAddonRowChange, isAddonExpired } from '../../../../helpers/addonPeriod';
import { GRANT_ROW_FIELDS, firstInvalidGrantIndex, prepareLoadedGrants } from '../../../../helpers/grantRows';
import { dayToUtcMidnight, findReplacingMaintenance, isCommunityProduct, todayAtUtcMidnight } from '../../../../helpers/communityMaintenance';
import ExpiredProductAddons from './ExpiredProductAddons';
import ApplyEditionModal from './ApplyEditionModal';
import MoveProductModal from './MoveProductModal';
import { canMoveProduct } from '../../../../helpers/productMove';
import { getSettingValue } from '../../../../helpers/settings';
import { getAssemblyTests } from '../../../../services/products';
import DateFormatterService from '../../../../helpers/DateFormatter';
import MaintenanceStatusFormatter from '../../../../helpers/MaintenanceStatusFormatter';
import PostInstallCheckLogsModal from '../../../../components/PostInstallCheckLogsModal';
import { getProductAddons } from '../../../../data/productAddons/actions';

const defaultAddon = () => {
	return {
		uuid: Utils.uuidv4(),
		addonId: '',
		duration: '',
		durationType: 'months',
		addonType: 'Time-based',
		startDate: null,
		startOnActivation: false,
		tierId: ''
	};
};

const ProductForm = (props) => {
	const {
		productAddons: availableProductAddons,
		getProductAddons,
		productModels,
		companiesWithEndUsers,
		maintenancePlansOptions,
		id,
		formName,
		isEditing,
		isSendingRequest,
		generatedActivationKey,
		getActivationCode,
		changeFormFieldInState,
		touchFields,
		addonErrors,
		submit,
		handleSubmit,
		product,
		initialValues,
		maintenanceTotalDuration,
		maxMaintenanceExtension,
		getProductById,
		pushSuccessfulNotification
	} = props;
	const [virtualAppliance, setVirtualAppliance] = useState(null);
	const [productAddons, setProductAddons] = useState(prepareLoadedGrants(product?.productAddons));
	const [expiredProductAddons, setExpiredProductAddons] = useState([]);
	// The one license row showing its fields; the others are a line of text each.
	const [openAddonUuid, setOpenAddonUuid] = useState(null);
	const [showExpiredAddons, setShowExpiredAddons] = useState(false);
	const [showApplyEdition, setShowApplyEdition] = useState(false);
	const [showMoveProduct, setShowMoveProduct] = useState(false);
	const [maintenanceServices, setMaintenanceServices] = useState(product?.maintenanceServices ?? initialValues.maintenanceServices);
	const [validationMessage, setValidationMessage] = useState(null);
	const [showAssemblyLogsModal, setShowAssemblyLogsModal] = useState(false);
	const [assemblyLogs, setAssemblyLogs] = useState('');
	const navigate = useNavigate();
	const expirationDate = (product?.activationDate && product?.maintenanceServices) ? DateFormatterService.formatDateToSpecificFormat(product?.maintenanceServices[0], 'expiresOn', 'DD/MM/YYYY') : null;


	const [activationDate, setActivationDate] = useState(product?.activationDate ?? null);
	
	function checkDateStatus(dateString) {
		const inputDate = moment(dateString, 'DD/MM/YYYY');
		if (!inputDate.isValid()) {
			return 'Invalid date format';
		}
		const today = moment().startOf('day');
		if (inputDate.isBefore(today)) {
			return 'Expired';
		} else {
			return 'Active';
		}
	}

	useEffect(() => {
		getProductAddons();
	}, []);

	useEffect(() => {
		if (product && product.assemblyTestStatus) {
			getAssemblyTests(product.serialNumber)
				.then((response) => {
					setAssemblyLogs(response.data[0].logs);
				})
				.catch((error) => {
					setAssemblyLogs('Failed to fetch assembly logs.');
				});
		}
	}, []);

	// A virtual appliance has no serial and gets an activation code. It no longer gets Compute and Storage: from 6.1
	// a software product is licensed through an edition, applied with "Apply edition" (SYN-3464).
	useEffect(() => {
		if (virtualAppliance) {
			changeFormFieldInState('virtualAppliance', true);
			if (!product) {
				changeFormFieldInState('serialNumber', null);
			}
			if (!product?.activationCode) {
				getActivationCode();
			}
		} else {
			changeFormFieldInState('virtualAppliance', false);
		}
	}, [virtualAppliance]);

	useEffect(() => {
		if (!product) {
			setProductAddons([]);
			setMaintenanceServices(initialValues.maintenanceServices);
			return;
		}

		const selectedProductModel = productModels.find((productModel) => { return productModel.id === product.modelId; });
		if (selectedProductModel) {
			setVirtualAppliance(selectedProductModel.virtualAppliance);
		}
		setProductAddons(prepareLoadedGrants(product.productAddons));
		setMaintenanceServices(product.maintenanceServices);
	}, [productModels, product]);

	useEffect(() => {
		changeFormFieldInState('productAddons', productAddons);
		setExpiredProductAddons(productAddons.filter((addon) => { return isAddonExpired(addon.endDate) && addon.isRenewed; }));
	}, [productAddons]);

	useEffect(() => {
		changeFormFieldInState('maintenanceServices', maintenanceServices);
	}, [maintenanceServices]);

	useEffect(() => {
		changeFormFieldInState('activationCode', generatedActivationKey);
	}, [generatedActivationKey]);

	// Addons
	const addAddon = useCallback(() => {
		const addon = defaultAddon();

		setProductAddons([...productAddons, addon]);
		setOpenAddonUuid(addon.uuid);
	}, [productAddons]);

	const toggleAddon = useCallback((uuid) => {
		setOpenAddonUuid((current) => { return current === uuid ? null : uuid; });
	}, []);

	const applyEditionRows = useCallback((rows) => {
		setProductAddons([...productAddons, ...rows]);
	}, [productAddons]);

	const changeAddon = useCallback((addon) => {
		setProductAddons(applyAddonRowChange(productAddons, addon));
	}, [productAddons]);

	const removeAddon = useCallback((uuid) => {
		setProductAddons(productAddons.filter((addon) => { return addon.uuid !== uuid; }));
		setOpenAddonUuid((current) => { return current === uuid ? null : current; });
	}, [productAddons]);

	// Maintenance services
	const defaultMaintenanceService = useCallback(() => {
		return {
			uuid: Utils.uuidv4(),
			maintenancePlanId: '',
			hasSerenity: false,
			duration: '',
			durationType: 'months',
			saleValue: '',
			isRenewal: true,
			quoteNumber: '',
		};
	}, []);

	const changeMaintenanceService = useCallback((maintenanceService, index) => {
		setMaintenanceServices(maintenanceServices.map((service) => { return service.uuid === index ? maintenanceService : service; }));
	}, [maintenanceServices]);

	const changeProductModel = useCallback((value) => {
		const selectedProductModel = productModels.find((productModel) => {
			return productModel.id === value;
		});

		if (selectedProductModel) {
			setVirtualAppliance(selectedProductModel.virtualAppliance);
		}
	}, [productModels]);

	// On a Community product the added row is the paid maintenance that replaces Community Support, and it starts on a
	// chosen day, today unless changed (SYN-3454).
	const addMaintenanceService = useCallback(() => {
		const maintenanceService = defaultMaintenanceService();

		if (isCommunityProduct(maintenanceServices)) {
			maintenanceService.startDate = todayAtUtcMidnight();
		}
		setMaintenanceServices([...maintenanceServices, maintenanceService]);
	}, [maintenanceServices]);

	const replacingMaintenance = findReplacingMaintenance(maintenanceServices);

	const changeReplacingMaintenanceStart = useCallback((day) => {
		changeMaintenanceService({ ...replacingMaintenance, startDate: dayToUtcMidnight(day) }, replacingMaintenance.uuid);
	}, [replacingMaintenance, changeMaintenanceService]);

	const removeMaintenanceRenewal = useCallback((index) => {
		setMaintenanceServices(maintenanceServices.filter((service) => { return service.uuid !== index; }));
	}, [maintenanceServices]);

	// The move is saved on its own; the product is loaded again to show the new serial (SYN-3453).
	const productMoved = useCallback((serialNumber) => {
		setShowMoveProduct(false);
		pushSuccessfulNotification(`Product moved to ${serialNumber}`);
		getProductById(product.id);
	}, [product]);

	const copyToClipboard = (inputId) => {
		const copyText = document.getElementById(inputId);
		copyText.select();
		document.execCommand('copy');
	};

	const renderAssemblyTestResults = (product) => {
		return product?.assemblyTestStatus ? (
			<>
				{renderPostInstallChecksSectionStatusPill(product.assemblyTestStatus)}
				<Button
					className="ms-2 py-0"
					role="tertiary"
					onClick={() => {
						return setShowAssemblyLogsModal(true);
					}}
				>
					<Icon className="me-2" name="fa fa-eye" color="#4f5ed7" /> View Logs
				</Button>
			</>
		) : (
			<span>Not performed for this machine</span>
		);
	};

	const renderPostInstallChecksSectionStatusPill = (status) => {
		return status === 'success' ? (
			<Tooltip text="Tests are successful" placement="top">
				<Badge appearance="success"><Icon name="fa fa-check" /></Badge>
			</Tooltip>
		) : (
			<Tooltip text="Tests have failed" placement="top">
				<Badge appearance="danger"><Icon name="fa fa-times" /></Badge>
			</Tooltip>
		);
	};

	const isFormValid = useCallback((product) => {
		// A paid maintenance replacing Community Support does not add to the 99-year Community row, so the maximum
		// number of years is not checked against it (SYN-3454).
		const extendsMaintenance = product.maintenanceServices.length > 1 && !findReplacingMaintenance(product.maintenanceServices);

		if (extendsMaintenance) {
			if (!ProductValidationService.isSerialNumberValid(product.serialNumber) && !ProductValidationService.isDurationValid(maxMaintenanceExtension, maintenanceTotalDuration)) {
				setValidationMessage('That doesn\'t look like a correct serial number and the maximum number of years has been exceeded. Continue?');
				return false;
			}
			if (!ProductValidationService.isDurationValid(maxMaintenanceExtension, maintenanceTotalDuration)) {
				setValidationMessage('Maximum number of years has been reached. Continue?');
				return false;
			}
		} else if (!ProductValidationService.isSerialNumberValid(product.serialNumber) && !product.virtualAppliance) {
			setValidationMessage('That doesn\'t look like a correct serial number. Continue?');
			return false;
		}

		return true;
	}, [maintenanceTotalDuration, maxMaintenanceExtension]);

	/*
	 * redux-form only blocks the save on fields that are on screen, and the overview shows one row's fields at a time:
	 * a refused row is opened with its fields touched, so the operator sees what to fix, and the save waits.
	 */
	const trySubmit = useCallback((product) => {
		const invalidIndex = firstInvalidGrantIndex(ProductValidationService.validateAddons(product, availableProductAddons), product.productAddons);

		if (invalidIndex !== -1) {
			setOpenAddonUuid(product.productAddons[invalidIndex].uuid);
			touchFields(formName, ...GRANT_ROW_FIELDS.map((field) => { return `productAddons[${invalidIndex}].${field}`; }));
			return;
		}

		if (isFormValid(product)) {
			return submit(product);
		}
	}, [availableProductAddons, formName, isFormValid, submit, touchFields]);

	const submitWarningModal = useCallback((product) => {
		submit(product);
		setValidationMessage(null);
	}, []);

	return (
		<>
			<form id={id} className="form page-fill__body" onSubmit={handleSubmit(trySubmit)}>
				<fieldset className="page-fill__scroll border-0">
					<Row>
						<Col xs={8}>
							<h4 className="mt-4">Appliance</h4>
							<Row>
								<Col xs={3}>
									<Form.select
										label="Model"
										name="modelId"
										options={SelectOptionsRenderer.renderProductModel(productModels)}
										onValueChange={changeProductModel}
										emptyOption="Please select"
										required
									/>
								</Col>
								<Col xs={3}>
									{/* A virtual appliance's serial is set by the machine: it is read, never typed, so it can be copied. */}
									{virtualAppliance && product?.serialNumber && <div className="float-end">
										<Tooltip text="Copy serial number" placement="top" trigger="click">
											<Button className="p-0" role="tertiary" onClick={() => { return copyToClipboard('serialNumber'); }}>
												<Icon name="fa fa-copy" color="black" />
											</Button>
										</Tooltip>
									</div>}
									<Form.text
										id="serialNumber"
										name="serialNumber"
										label="Serial number"
										readOnly={virtualAppliance}
										required={!virtualAppliance}
										placeholder={virtualAppliance ? 'No serial attached' : ''}
									/>
									{isEditing && canMoveProduct(product, productModels) && <div className="form-text mb-3" style={{ marginTop: '-0.75rem' }}>
										<span className="add-note-btn-support" role="button" onClick={() => { setShowMoveProduct(true); }}>
											<i className="fa fa-exchange me-2" />Move to another machine
										</span>
									</div>}
								</Col>
								<Col xs={3}>
									<Form.text name="saleValue" label="Sale value" placeholder="EUR, e.g. 1456.43" />
								</Col>
								<Col xs={3}>
									<Form.text name="osVersion" label="OS Version" disabled />
								</Col>
							</Row>

							<hr />
							{/* The actions sit by the title so they stay put as rows are added. */}
							<Row className="d-flex align-items-center justify-content-between mt-4 mb-2">
								<h4 className="col-auto mb-0">Licenses</h4>
								<Col xs={'auto'} className="d-flex align-items-center gap-4" style={{ fontSize: '14px' }}>
									<span className="add-note-btn-support" role="button" onClick={addAddon}>
										<i className="fa fa-plus me-2" />Add license
									</span>
									<span className="add-note-btn-support" role="button" onClick={() => { setShowApplyEdition(true); }}>
										<i className="fa fa-cubes me-2" />Apply edition
									</span>
									{expiredProductAddons.length > 0 && <Button role="tertiary" className="py-0" onClick={() => { setShowExpiredAddons(true); }}>See expired licenses</Button>}
								</Col>
							</Row>
							{productAddons.filter((addon) => { return !addon.isRenewed; }).length > 0 && <Row className="fw-bold mb-2">
								<Col xs={3}>License</Col>
								<Col xs={3}>Period</Col>
								<Col xs={2}>Tier</Col>
								<Col xs={2}>Status</Col>
								<Col xs={2}></Col>
							</Row>}
							{productAddons.map((addon, index) => {
								if (addon.isRenewed && addon.isExpired) {
									return null; // Skip renewed add-ons
								}

								return (
									<ProductAddon
										key={addon.uuid}
										index={index}
										addon={addon}
										isOpen={addon.uuid === openAddonUuid}
										onToggle={() => { toggleAddon(addon.uuid); }}
										errors={addonErrors[index]}
										removeAddon={removeAddon}
										changeAddon={changeAddon}
										changeProp={changeFormFieldInState}
									/>
								);
							})}

							<hr />
							<Row className="d-flex align-items-start justify-content-between mt-4">
								<h4 className="col-auto">Maintenance</h4>
								{
									product?.activationDate ?
										(checkDateStatus(expirationDate) !== 'Expired' ?
											<Badge appearance={MaintenanceStatusFormatter.setExpiresOnBadge('Active')}>
												{`Expires on: ${expirationDate}`}
											</Badge> :
											<Badge appearance={MaintenanceStatusFormatter.setExpiresOnBadge('Expired')}>
												{`Expired since: ${expirationDate}`}
											</Badge>)
										: null
								}
							</Row>

							{!isEditing && <ProductMaintenancePlan
								index={0}
								maintenancePlansOptions={maintenancePlansOptions}
								maintenanceService={maintenanceServices[0]}
								isRenewal={false}
								change={changeFormFieldInState}
								changeMaintenanceService={changeMaintenanceService}
							/>}

							{isEditing && maintenanceServices.map((maintenanceService, index) => {
								return (
									<ProductMaintenancePlan
										key={maintenanceService.uuid}
										index={index}
										maintenancePlansOptions={maintenancePlansOptions}
										maintenanceService={maintenanceService}
										isRenewal={index > 0}
										change={changeFormFieldInState}
										changeMaintenanceService={changeMaintenanceService}
										deleteMaintenanceService={removeMaintenanceRenewal}
									/>
								);
							})}
							{isEditing && replacingMaintenance && <CommunityReplacementNotice
								index={maintenanceServices.indexOf(replacingMaintenance)}
								startDate={replacingMaintenance.startDate}
								changeProp={changeFormFieldInState}
								onStartDateChange={changeReplacingMaintenanceStart}
							/>}
							{isEditing && maintenanceServices && <Row>
								<Col xs={'auto'} className="pt-2 add-note-btn-support" style={{ fontSize: '14px' }} onClick={addMaintenanceService}>
									<i className="fa fa-plus me-2" />Extend maintenance
								</Col>
							</Row>}
						</Col>
						<Col xs={4}>
							<h4 className="mt-4">Activation details</h4>
							<Row>
								<Col xs={6}>
									<Form.select
										label="End-user"
										name="userId"
										options={SelectOptionsRenderer.renderCompaniesWithUsers(companiesWithEndUsers)}
										emptyOption="Not set"
										disabled={virtualAppliance}
									/>
								</Col>
								<Col xs={6}>
									<div className="mb-3">
										<Form.datepicker
											formName={formName}
											calendarClassName="activation-details__date-picker"
											id="activationDate"
											name="activationDate"
											label="Activation date"
											placeholderText="Not set"
											placement="left"
											initValue={activationDate ? activationDate : null}
											autoComplete="off"
											disabled={virtualAppliance && !isEditing}
											onValueChange={(newDate) => {
												setActivationDate(newDate ? moment(newDate) : null);
											}}
										/>
									</div>
								</Col>
							</Row>
							{virtualAppliance && <Row>
								<Col xs={12}>
									<div className="float-end">
										<Tooltip text="Copy activation key" placement="top" trigger="click">
											<Button className="p-0 me-2" role="tertiary" onClick={() => { return copyToClipboard('activationCode'); }}>
												<Icon name="fa fa-copy" color="black" />
											</Button>
										</Tooltip>
										{!isEditing && <Button className="p-0 me-2" role="tertiary" onClick={() => { return getActivationCode(); }}>
											<Icon name="fa fa-sync" color="black" />
										</Button>}
									</div>
									<Form.text
										id="activationCode"
										name="activationCode"
										label="Activation Code"
										value={product?.activationCode ?? ''}
										placeholder="Not set"
										readOnly
									/>
								</Col>
							</Row>}

							<Row>
								<Col xs={12}>
									<Form.textarea label="Notes" id="notes" name="notes" rows={4} required={virtualAppliance} />
									{virtualAppliance && <div className="form-text mb-3" style={{ marginTop: '-0.75rem' }}>Required for a virtual appliance.</div>}
								</Col>
							</Row>

							{/* A virtual appliance is never assembled, so it has no test results to show. */}
							{product && !virtualAppliance && <Row>
								<label className="col-auto pt-2"><strong>Assembly test results:</strong></label>
								<div className="col pt-2">
									{renderAssemblyTestResults(product)}
								</div>
							</Row>}
						</Col>
					</Row>
				</fieldset>
				{/* The actions stay in view while the operator scrolls a long product. */}
				<Row className="page-fill__footer justify-content-end bg-white border-top py-3">
					<Col xs={'auto'} className="p-0">
						<Button role="secondary" onClick={() => { navigate('/maintenance-service/maintenance-plans'); }}>Exit without saving</Button>
					</Col>
					<Col xs={'auto'}>
						<Button disabled={isSendingRequest} type="submit">
							{isSendingRequest ? <Icon name="fa fa-spinner fa-spin" /> : isEditing ? 'Save details' : 'Create'}
						</Button>
					</Col>
				</Row>
			</form>

			<PostInstallCheckLogsModal
				show={showAssemblyLogsModal}
				onHide={() => {
					return setShowAssemblyLogsModal(false);
				}}
				userRole='operations'
				assemblyLogs={assemblyLogs}
				serialNumber={product?.serialNumber}
			/>

			<WarningModal
				showWarningModal={!!validationMessage}
				content={validationMessage}
				closeWarningModal={() => { setValidationMessage(null); }}
				onSubmit={handleSubmit(submitWarningModal)}
			/>

			<ApplyEditionModal
				show={showApplyEdition}
				catalog={availableProductAddons}
				onHide={() => { setShowApplyEdition(false); }}
				onApply={applyEditionRows}
			/>

			<MoveProductModal
				show={showMoveProduct}
				product={product}
				onHide={() => { setShowMoveProduct(false); }}
				onMoved={productMoved}
			/>

			<Modal
				enforceFocus={false}
				show={showExpiredAddons}
				onHide={() => { setShowExpiredAddons(false); }}
				content={<ExpiredProductAddons addons={expiredProductAddons} />}
				title="Expired licenses"
				size="lg"
			/>
		</>);
};

const mapStateToProps = (state, ownProps) => {
	const selectedProduct = state.products.selectedProduct;
	const syncErrors = getFormSyncErrors(ownProps.formName)(state);
	const maintenanceTotalDuration = selectedProduct?.maintenanceServices?.slice(1).reduce(
		(acc, maintenanceExtension) => {
			acc += parseInt(maintenanceExtension.duration);
			return acc;
		},
		parseInt(selectedProduct?.maintenanceServices[0].duration)
	);

	return {
		generatedActivationKey: state.products.activationKey,
		productAddons: state.productAddons.data,
		// What the form's validation says of each license row, for the overview's Status column.
		addonErrors: syncErrors?.productAddons ?? [],
		productModels: state.productModels.models,
		companiesWithEndUsers: state.companies.companiesWithEndUsers,
		maintenancePlansOptions: state.maintenancePlansOptions.maintenancePlansOptions.allMaintenancePlansOptions,
		maintenanceTotalDuration: maintenanceTotalDuration,
		maxMaintenanceExtension: getSettingValue(state.settings.data, 'maxMaintenanceExtension') ?? 36,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		getActivationCode: () => { return dispatch(getActivationCode()); },
		setSelectedProduct: (data) => { return dispatch(getProductSuccess(data)); },
		getProductAddons: () => { return dispatch(getProductAddons()); },
		touchFields: (formName, ...fields) => { return dispatch(touch(formName, ...fields)); },
		getProductById: (productId) => { return dispatch(getProductById(productId)); },
		pushSuccessfulNotification: (message) => { return dispatch(pushSuccessfulNotification(message)); }
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductForm);

ProductForm.propTypes = {
	productModels: PropTypes.array,
	companiesWithEndUsers: PropTypes.array,
	maintenancePlansOptions: PropTypes.array,
	id: PropTypes.string.isRequired,
	formName: PropTypes.string.isRequired,
	isEditing: PropTypes.bool.isRequired,
	isSendingRequest: PropTypes.bool,
	getActivationCode: PropTypes.func.isRequired,
	changeFormFieldInState: PropTypes.func.isRequired,
	submit: PropTypes.func.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	product: PropTypes.object,
	initialValues: PropTypes.object
};
